// Login admin con flow 2FA TOTP (Sprint 5).
// Stati locali: 'credentials' → 'verify_2fa' → completato (redirect)
// Se backend ritorna require_2fa_setup, redirect a wizard /security/2fa-setup.

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoginRequestSchema, type LoginRequest } from '@audiq/shared';
import { AudiqWordmark, RuleBicolor, ThemeToggle } from '@audiq/ui';
import { api, ApiClientError } from '../lib/api';

type Stage = 'credentials' | 'verify_2fa';

export function LoginPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>('credentials');
  const [partialToken, setPartialToken] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [twofaCode, setTwofaCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginRequest>({
    resolver: zodResolver(LoginRequestSchema),
    defaultValues: { email: '', password: '' },
  });

  async function onSubmit(data: LoginRequest) {
    setServerError(null);
    setSubmitting(true);
    try {
      const res = await api.login(data);
      if (res.flow === 'complete') {
        if (res.user.must_change_password) {
          navigate('/settings/security?force_change=1', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } else if (res.flow === 'require_2fa') {
        setPartialToken(res.partial_token);
        setStage('verify_2fa');
      } else if (res.flow === 'require_2fa_setup') {
        // Master in grace scaduto: forziamo setup
        setPartialToken(res.partial_token);
        navigate('/security/2fa-setup-required', {
          replace: true,
          state: { partial_token: res.partial_token },
        });
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.payload.code === 'ACCOUNT_LOCKED') {
          setServerError('Account temporaneamente bloccato. Riprova fra 15 minuti.');
        } else if (err.payload.code === 'RATE_LIMITED') {
          setServerError('Troppi tentativi. Attendi un minuto.');
        } else {
          setServerError('Credenziali non valide.');
        }
      } else {
        setServerError('Errore di rete. Riprova.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onVerify2fa(e: React.FormEvent) {
    e.preventDefault();
    if (!partialToken || twofaCode.length < 6) return;
    setServerError(null);
    setSubmitting(true);
    try {
      const res = await api.verify2fa({
        partial_token: partialToken,
        code: twofaCode.toUpperCase().trim(),
        is_recovery_code: useRecoveryCode,
      });
      if (res.flow === 'complete') {
        if (res.user.must_change_password) {
          navigate('/settings/security?force_change=1', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.payload.code === 'INVALID_PARTIAL') {
          setServerError('Sessione scaduta. Rifai il login.');
          setStage('credentials');
        } else if (err.payload.code === 'INVALID_CODE') {
          setServerError('Codice non valido. Riprova.');
        } else {
          setServerError(err.payload.error);
        }
      } else {
        setServerError('Errore di rete.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="flex flex-1 flex-col"
      style={{ background: 'var(--surface)', color: 'var(--ink)' }}
    >
      <div className="flex justify-end p-4">
        <ThemeToggle size="sm" />
      </div>

      <div className="flex flex-1 items-center justify-center px-4 py-8">
        <div className="w-full max-w-sm">
          <header className="mb-8 text-center">
            <AudiqWordmark size="xl" />
            <p className="mt-3 mono" style={{ color: 'var(--mute)' }}>
              {stage === 'credentials' ? 'Console di amministrazione' : 'Verifica in due passaggi'}
            </p>
            <RuleBicolor className="mt-6" />
          </header>

          {stage === 'credentials' ? (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-4 rounded-lg border p-6"
              style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
              noValidate
            >
              <div>
                <label
                  htmlFor="email"
                  className="mb-1 block text-sm font-medium"
                  style={{ color: 'var(--ink)' }}
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  {...register('email')}
                  className="w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2"
                  style={{
                    background: 'var(--paper)',
                    borderColor: 'var(--line)',
                    color: 'var(--ink)',
                    /* @ts-expect-error CSS custom prop */
                    '--tw-ring-color': 'var(--primary)',
                  }}
                  placeholder="email@dominio.it"
                />
                {errors.email && (
                  <p className="mt-1 text-xs" style={{ color: 'var(--danger)' }}>
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="mb-1 block text-sm font-medium"
                  style={{ color: 'var(--ink)' }}
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  {...register('password')}
                  className="w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2"
                  style={{
                    background: 'var(--paper)',
                    borderColor: 'var(--line)',
                    color: 'var(--ink)',
                    /* @ts-expect-error CSS custom prop */
                    '--tw-ring-color': 'var(--primary)',
                  }}
                />
                {errors.password && (
                  <p className="mt-1 text-xs" style={{ color: 'var(--danger)' }}>
                    {errors.password.message}
                  </p>
                )}
              </div>

              {serverError && (
                <div
                  className="rounded-md p-3 text-sm"
                  style={{
                    background: 'color-mix(in oklab, var(--danger) 12%, transparent)',
                    color: 'var(--danger)',
                    border: '1px solid color-mix(in oklab, var(--danger) 30%, transparent)',
                  }}
                >
                  {serverError}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-md px-4 py-2 text-sm font-medium shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60"
                style={{ background: 'var(--primary)', color: '#fff' }}
              >
                {submitting ? 'Accesso…' : 'Accedi'}
              </button>
            </form>
          ) : (
            <form
              onSubmit={onVerify2fa}
              className="space-y-4 rounded-lg border p-6"
              style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
            >
              <p style={{ color: 'var(--mute)', fontSize: '14px' }}>
                {useRecoveryCode
                  ? 'Inserisci uno dei tuoi codici di recovery (formato XXXX-XXXX).'
                  : 'Inserisci il codice a 6 cifre dalla tua app authenticator.'}
              </p>

              <div>
                <label
                  htmlFor="twofa-code"
                  className="mb-1 block text-sm font-medium"
                  style={{ color: 'var(--ink)' }}
                >
                  {useRecoveryCode ? 'Codice di recovery' : 'Codice 2FA'}
                </label>
                <input
                  id="twofa-code"
                  type="text"
                  inputMode={useRecoveryCode ? 'text' : 'numeric'}
                  autoComplete="one-time-code"
                  autoFocus
                  value={twofaCode}
                  maxLength={useRecoveryCode ? 12 : 6}
                  onChange={(e) => setTwofaCode(e.target.value)}
                  className="mono w-full rounded-md border px-3 py-2 text-center text-lg tracking-widest shadow-sm focus:outline-none focus:ring-2"
                  style={{
                    background: 'var(--paper)',
                    borderColor: 'var(--line)',
                    color: 'var(--ink)',
                    /* @ts-expect-error CSS custom prop */
                    '--tw-ring-color': 'var(--primary)',
                    letterSpacing: useRecoveryCode ? '0.15em' : '0.3em',
                  }}
                  placeholder={useRecoveryCode ? 'XXXX-XXXX' : '000000'}
                />
              </div>

              <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--mute)' }}>
                <input
                  type="checkbox"
                  checked={useRecoveryCode}
                  onChange={(e) => {
                    setUseRecoveryCode(e.target.checked);
                    setTwofaCode('');
                  }}
                />
                Usa un codice di recovery
              </label>

              {serverError && (
                <div
                  className="rounded-md p-3 text-sm"
                  style={{
                    background: 'color-mix(in oklab, var(--danger) 12%, transparent)',
                    color: 'var(--danger)',
                    border: '1px solid color-mix(in oklab, var(--danger) 30%, transparent)',
                  }}
                >
                  {serverError}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || twofaCode.length < 6}
                className="w-full rounded-md px-4 py-2 text-sm font-medium shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60"
                style={{ background: 'var(--primary)', color: '#fff' }}
              >
                {submitting ? 'Verifica…' : 'Verifica e accedi'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setStage('credentials');
                  setTwofaCode('');
                  setServerError(null);
                }}
                className="w-full text-center text-xs underline"
                style={{ color: 'var(--mute)' }}
              >
                Annulla e riparti
              </button>
            </form>
          )}

          <p className="mt-6 text-center mono" style={{ color: 'var(--mute)' }}>
            Audiq · Arkenù snc
          </p>
        </div>
      </div>
    </div>
  );
}
